/**
 * API Client for Beacon Backend
 */
import axios, { AxiosInstance, AxiosError } from 'axios'
import type {
  Agent,
  AgentListResponse,
  AgentCreateRequest,
  AgentUpdateRequest,
  GenerateConfigRequest,
  GenerateConfigResponse,
  TestAgentRequest,
  TestAgentResponse,
  InvokeAgentRequest,
  InvokeAgentResponse,
  Deployment,
  DeploymentListResponse,
  DeploymentCreateRequest,
  DeploymentProfileCatalog,
  DeploymentResourceManifest,
  CreateDeploymentResult,
  Tool,
  ToolListResponse,
  User,
  UserCreateRequest,
  ToolGenerateRequest,
  SuccessResponse,
  HealthCheckResponse,
} from '@/types/api'
import { API_BASE_URL } from '@/utils/runtime'
import { clearAuthSession, getAccessToken, getSessionInfo, setAuthSession } from '@/utils/auth'
import { getSelectedAwsLinkId } from '@/utils/beaconAwsLink'

// In production the frontend is served by the FastAPI backend on the same
// origin, so all API calls use relative paths (BASE_URL = '').
// In local dev the Vite proxy rewrites /api → http://localhost:8000 so
// relative paths work there too.  Only use an explicit URL if VITE_API_URL
// is set to a non-empty value (e.g. pointing at a remote dev backend).
const BASE_URL = API_BASE_URL

class APIClient {
  private client: AxiosInstance

  /** Expose the underlying Axios instance for consumers that make raw calls
   *  (e.g. Settings, UserManagement). Using this ensures all requests share
   *  the same auth interceptors and token-refresh logic. */
  get rawClient() {
    return this.client
  }

  constructor() {
    this.client = axios.create({
      baseURL: BASE_URL,
      timeout: 120000, // 120 seconds (default; LLM calls use a longer per-request timeout)
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    })

    // Request interceptor to add auth token + selected AWS link for Beacon routes
    this.client.interceptors.request.use(
      (config) => {
        const token = getAccessToken()
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        const awsLinkId = getSelectedAwsLinkId()
        const url = String(config.url || '')
        if (awsLinkId && url.includes('/api/v1/beacon/')) {
          config.headers['X-Beacon-Aws-Link'] = awsLinkId
        }
        // Never attach deployment credentials to logs / analytics — Axios does not log bodies by default.
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor — attempt silent token refresh on 401, then redirect
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as typeof error.config & { _retry?: boolean }
        if (error.response?.status === 401 && !originalRequest?._retry) {
          originalRequest!._retry = true
          try {
            const refreshToken = getSessionInfo().refreshToken
            if (!refreshToken) throw new Error('No refresh token')

            const { data } = await axios.post(`${BASE_URL}/api/v1/auth/refresh`, {
              refresh_token: refreshToken,
            })

            const { access_token, id_token, refresh_token } = data
            setAuthSession({
              accessToken: access_token,
              idToken: id_token,
              refreshToken: refresh_token ?? refreshToken,
            })

            originalRequest!.headers!['Authorization'] = `Bearer ${access_token}`
            return this.client(originalRequest!)
          } catch {
            // Refresh failed — clear tokens and redirect to login
            this.handleUnauthorized()
          }
        }
        return Promise.reject(error)
      }
    )
  }

  private handleUnauthorized() {
    clearAuthSession()
    // Navigate to login without a full page reload where possible
    window.location.href = '/login'
  }

  // Health Check
  async healthCheck(): Promise<HealthCheckResponse> {
    const { data } = await this.client.get<HealthCheckResponse>('/health')
    return data
  }

  // Agent endpoints
  async generateConfig(
    request: GenerateConfigRequest
  ): Promise<GenerateConfigResponse> {
    // LLM-backed generation via Bedrock can take 45-90 s for complex prompts.
    // Use a dedicated per-request timeout that overrides the client default.
    const { data } = await this.client.post<GenerateConfigResponse>(
      '/api/v1/agents/generate-config',
      request,
      { timeout: 150000 } // 150 seconds
    )
    return data
  }

  async createAgent(request: AgentCreateRequest): Promise<Agent> {
    const { data } = await this.client.post<Agent>('/api/v1/agents', request)
    return data
  }

  async listAgents(params?: {
    page?: number
    page_size?: number
    status_filter?: string
  }): Promise<AgentListResponse> {
    const { data } = await this.client.get<AgentListResponse>('/api/v1/agents', {
      params,
    })
    return data
  }

  async getAgent(agentId: string): Promise<Agent> {
    const { data } = await this.client.get<Agent>(`/api/v1/agents/${agentId}`)
    return data
  }

  async updateAgent(
    agentId: string,
    request: AgentUpdateRequest
  ): Promise<Agent> {
    const { data } = await this.client.put<Agent>(
      `/api/v1/agents/${agentId}`,
      request
    )
    return data
  }

  async deleteAgent(agentId: string): Promise<SuccessResponse> {
    const { data } = await this.client.delete<SuccessResponse>(
      `/api/v1/agents/${agentId}`
    )
    return data
  }

  async testAgent(
    agentId: string,
    request: TestAgentRequest
  ): Promise<TestAgentResponse> {
    const { data } = await this.client.post<TestAgentResponse>(
      `/api/v1/agents/${agentId}/test`,
      request
    )
    return data
  }

  async invokeAgent(
    agentId: string,
    request: InvokeAgentRequest
  ): Promise<InvokeAgentResponse> {
    const { data } = await this.client.post<InvokeAgentResponse>(
      `/api/v1/agents/${agentId}/invoke`,
      request
    )
    return data
  }

  // Deployment endpoints
  /**
   * Start a stack deployment. Pass `idempotencyKey` so retries / double-submit reuse the same key
   * and the server returns the existing deployment (HTTP 200, see Idempotency-Key header).
   */
  async createDeployment(
    request: DeploymentCreateRequest,
    options?: { idempotencyKey?: string }
  ): Promise<CreateDeploymentResult> {
    const headers: Record<string, string> = {}
    if (options?.idempotencyKey) {
      headers['Idempotency-Key'] = options.idempotencyKey
    }
    const res = await this.client.post<Deployment>('/api/v1/deployments', request, {
      headers,
      validateStatus: (s) => s === 200 || s === 201,
    })
    const hdr = res.headers as Record<string, string | undefined>
    const requestId = hdr['x-request-id'] ?? hdr['X-Request-ID'] ?? null
    return {
      deployment: res.data,
      httpStatus: res.status,
      requestId,
    }
  }

  async getDeploymentResourceManifest(
    request: DeploymentCreateRequest
  ): Promise<DeploymentResourceManifest> {
    const { data } = await this.client.post<DeploymentResourceManifest>(
      '/api/v1/deployments/resource-manifest',
      request
    )
    return data
  }

  async listDeploymentProfiles(): Promise<DeploymentProfileCatalog> {
    const { data } = await this.client.get<DeploymentProfileCatalog>('/api/v1/deployments/profiles')
    return data
  }

  async listDeployments(params?: {
    agent_id?: string
  }): Promise<DeploymentListResponse> {
    const { data } = await this.client.get<DeploymentListResponse>(
      '/api/v1/deployments',
      { params }
    )
    return data
  }

  async getDeployment(deploymentId: string): Promise<Deployment> {
    const { data } = await this.client.get<Deployment>(
      `/api/v1/deployments/${deploymentId}`
    )
    return data
  }

  async deleteDeployment(deploymentId: string): Promise<SuccessResponse> {
    const { data } = await this.client.delete<SuccessResponse>(
      `/api/v1/deployments/${deploymentId}`
    )
    return data
  }

  async retryDeployment(deploymentId: string): Promise<Deployment> {
    const { data } = await this.client.post<Deployment>(
      `/api/v1/deployments/${deploymentId}/retry`
    )
    return data
  }

  async updateDeploymentStatus(
    deploymentId: string,
    statusUpdate: Record<string, any>
  ): Promise<Deployment> {
    const { data } = await this.client.post<Deployment>(
      `/api/v1/deployments/${deploymentId}/update-status`,
      statusUpdate
    )
    return data
  }

  // Tool endpoints
  async listTools(params?: {
    category?: string
    search?: string
  }): Promise<ToolListResponse> {
    const { data } = await this.client.get<ToolListResponse>('/api/v1/tools', {
      params: { ...params, _t: Date.now() },
    })
    return data
  }

  async getTool(toolId: string): Promise<Tool> {
    const { data } = await this.client.get<Tool>(`/api/v1/tools/${toolId}`)
    return data
  }

  async listToolCategories(): Promise<{ categories: string[]; total: number }> {
    const { data } = await this.client.get('/api/v1/tools/categories/list')
    return data
  }

  async generateTool(request: ToolGenerateRequest): Promise<Tool> {
    const { data } = await this.client.post<Tool>('/api/v1/tools', request)
    return data
  }

  async updateTool(toolId: string, updates: Partial<Tool>): Promise<Tool> {
    const { data } = await this.client.put<Tool>(`/api/v1/tools/${toolId}`, updates)
    return data
  }

  async deleteTool(toolId: string): Promise<SuccessResponse> {
    const { data } = await this.client.delete<SuccessResponse>(`/api/v1/tools/${toolId}`)
    return data
  }

  // User endpoints
  async createUser(request: UserCreateRequest): Promise<User> {
    const { data } = await this.client.post<User>('/api/v1/users', request)
    return data
  }

  async getCurrentUser(): Promise<User> {
    const { data } = await this.client.get<User>('/api/v1/users/me')
    return data
  }

  async updateCurrentUser(updates: Partial<User>): Promise<User> {
    const { data } = await this.client.put<User>('/api/v1/users/me', updates)
    return data
  }
}

// Export singleton instance
export const apiClient = new APIClient()
